import { TimeRange } from '@/infra/time-range'

export interface SchedulableDay {
  /**
   * The date of the day.
   */
  date: Date

  /**
   * The time slots that are available for the day.
   */
  timeSlots: TimeRange[]
}

/**
 * Options for generating schedulable days.
 */
export interface GenerateSchedulableDaysOptions {
  /**
   * The current date and time.
   */
  now: Date

  /**
   * The number of days to show.
   * This is used in conjunction with the earliest time to determine the last day to show.
   */
  daysToShow: number

  /**
   * The duration of each time slot in minutes.
   */
  intervalInMinutes: number

  /**
   * The step between time slot start times in minutes.
   * Defaults to 30 if not specified.
   */
  stepInMinutes?: number

  /**
   * The hour of the day when scheduling starts (0-23).
   * Time slots will start at this hour.
   */
  startingHour: number

  /**
   * The hour of the day when scheduling ends (0-23).
   * Time slots will end at this hour.
   */
  endingHour: number

  /**
   * The earliest point in time from where to start generating schedulable days,
   * including the time slots within the first day.
   *
   * For example, if the earliest time is tomorrow at 10:00 AM, then the first day will have
   * time slots starting from 10:00 AM to 9:00 PM, rather than from today 8:00 AM which is
   * the usual start time of a day and earliest possible time slot.
   *
   * If the time part of this date does not fall exactly on a schedulable time slot,
   * the nearest future available time slot will be used.
   */
  earliestTime?: Date
}

/**
 * Generates schedulable days.
 *
 * @param options The options for generating schedulable days.
 * @returns The schedulable days, or an empty array if no time slots can
 * be generated (e.g. an interval longer than the daily scheduling window).
 */
export function generateSchedulableDays(
  options: GenerateSchedulableDaysOptions,
): SchedulableDay[] {
  const {
    now,
    daysToShow,
    intervalInMinutes,
    stepInMinutes = Math.min(intervalInMinutes, 30),
    earliestTime,
    startingHour = 8,
    endingHour = 21,
  } = options

  const dailyWindowInMinutes = (endingHour - startingHour) * 60

  if (
    intervalInMinutes <= 0 ||
    stepInMinutes <= 0 ||
    intervalInMinutes > dailyWindowInMinutes
  ) {
    return []
  }

  const schedulableDays: SchedulableDay[] = []

  // Determine starting date - use today if no earliestTime is specified,
  // or use the date component of earliestTime if provided
  let startDate = new Date(now)
  startDate.setHours(0, 0, 0, 0) // Reset to beginning of the day

  // If earliestTime is provided, use that date as the starting point
  if (earliestTime) {
    const earliestDate = new Date(earliestTime)
    earliestDate.setHours(0, 0, 0, 0) // Reset to beginning of the day
    if (earliestDate > startDate) {
      startDate = earliestDate
    }
  }

  // Scan forward day by day until the requested number of days is collected.
  // The scan is bounded so that days without slots can never turn into an
  // endless search.
  const maxDaysToScan = daysToShow * 2

  for (
    let dayOffset = 0;
    dayOffset < maxDaysToScan && schedulableDays.length < daysToShow;
    dayOffset++
  ) {
    const dayDate = new Date(startDate)
    dayDate.setDate(dayDate.getDate() + dayOffset)

    // Skip this day if the earliest time is past the last schedulable hour
    if (
      dayDate.toDateString() === earliestTime?.toDateString() &&
      earliestTime.getHours() >= endingHour
    ) {
      continue
    }

    // Generate all possible time slots for this day
    let slotStartHour = startingHour
    let slotStartMinute = 0

    // If this is the same day as earliestTime, adjust the start time
    if (dayDate.toDateString() === earliestTime?.toDateString()) {
      // Use the earliestTime's hour and minute, rounded up to the next slot boundary
      const earliestHour = earliestTime.getHours()
      const earliestMinute = earliestTime.getMinutes()

      // Calculate the nearest future time slot
      if (earliestMinute % stepInMinutes === 0) {
        // Exactly on a step boundary
        slotStartHour = Math.max(earliestHour, startingHour)
        slotStartMinute = earliestMinute
      } else {
        if (earliestHour < startingHour) {
          // If the earliest time is before the starting hour, use the starting hour
          slotStartHour = startingHour
          slotStartMinute = 0
        } else {
          // Round up to the next step boundary
          slotStartHour = earliestHour
          slotStartMinute =
            Math.ceil(earliestMinute / stepInMinutes) * stepInMinutes

          // If we went past 60 minutes, increment the hour
          if (slotStartMinute >= 60) {
            slotStartHour++
            slotStartMinute = 0
          }
        }
      }
    }

    // Generate time slots using the helper function
    const timeSlots = generateDayTimeSlots(
      dayDate,
      slotStartHour,
      slotStartMinute,
      endingHour,
      intervalInMinutes,
      stepInMinutes,
    )

    // Only add days that have time slots
    if (timeSlots.length > 0) {
      schedulableDays.push({
        date: dayDate,
        timeSlots,
      })
    }
  }

  return schedulableDays
}

/**
 * Generates time slots for a single day.
 *
 * @param date The date to generate time slots for
 * @param startHour The hour to start generating from
 * @param startMinute The minute to start generating from
 * @param endHour The hour to stop generating at
 * @param intervalInMinutes The interval between slots in minutes
 * @param stepInMinutes How many minutes each step covers
 * @returns Array of TimeRange objects representing the time slots
 */
function generateDayTimeSlots(
  date: Date,
  startHour: number,
  startMinute: number,
  endHour: number,
  intervalInMinutes: number,
  stepInMinutes: number,
): TimeRange[] {
  const timeSlots: TimeRange[] = []

  const cursor = new Date(date)
  cursor.setHours(startHour, startMinute, 0, 0)

  const endTime = new Date(date)
  endTime.setHours(endHour, 0, 0, 0)

  while (cursor < endTime) {
    const slotEnd = new Date(cursor)
    slotEnd.setMinutes(cursor.getMinutes() + intervalInMinutes)

    if (slotEnd <= endTime) {
      timeSlots.push(TimeRange.of(new Date(cursor), new Date(slotEnd)))
    }

    cursor.setMinutes(cursor.getMinutes() + stepInMinutes)
  }

  return timeSlots
}
